import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import Table, { type Header, type Row } from 'src/components/Table';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { can, Can, Subject } from 'src/hooks/usePermissions';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { formatTimeLabel } from 'src/utils/date';
import { days } from './constants';
import { SideDropActions } from 'src/components/SideDropActions';
import { Pencil } from '@phosphor-icons/react';
import { useEnvironment } from 'src/contexts/EnvironmentContext';

export const ListContactWindows = () => {
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const { role } = useEnvironment();
  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchContactWindows = async () => {
      setLoading(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
      } catch (error) {
        console.error('Failed to fetch contact windows:', error);
        setContactWindows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContactWindows();
  }, [get]);

  const formatDays = (daysArray?: number[]): string => {
    if (!daysArray || daysArray.length === 0) {
      return '-';
    }
    return daysArray
      .slice()
      .sort((a, b) => a - b)
      .map((dayValue) => {
        const dayOption = days.find((d) => d.value === dayValue);
        return dayOption ? dayOption.label : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const formatTimezone = (tz: string): string => {
    const timezoneMap: Record<string, string> = {
      'contact-local': "Contact's Local",
      'America/Los_Angeles': 'Pacific Time',
      'America/Denver': 'Mountain Time',
      'America/Chicago': 'Central Time',
      'America/New_York': 'Eastern Time',
    };
    return timezoneMap[tz] || tz;
  };

  const headers: Header[] = [
    { key: 'label', label: 'Label', width: '20%', disableSorting: true },
    {
      key: 'description',
      label: 'Description',
      width: '25%',
      disableSorting: true,
    },
    { key: 'days', label: 'Days', width: '15%', disableSorting: true },
    {
      key: 'timeRange',
      label: 'Time Range',
      width: '15%',
      disableSorting: true,
    },
    { key: 'timezone', label: 'Timezone', width: '15%', disableSorting: true },
    {
      key: 'usHolidays',
      label: 'US Holidays',
      width: '10%',
      align: 'center',
      disableSorting: true,
    },
    { key: 'actions', label: '', width: '10%', disableSorting: true },
  ];

  const rows: Row[] = contactWindows.map((window: WorkflowContactWindow) => ({
    label: window.label || '-',
    description: window.description || '-',
    days: formatDays(window.days),
    timeRange:
      window.startTime && window.endTime
        ? `${formatTimeLabel(window.startTime)} - ${formatTimeLabel(
            window.endTime,
          )}`
        : '-',
    timezone: (
      <div className="flex flex-col gap-1">
        <div>
          <span className="text-neutral-600 text-sm font-bold mr-1">
            Primary:
          </span>
          <span className="text-sm">{formatTimezone(window.primaryTz)}</span>
        </div>
        {window.fallbackTz && (
          <div>
            <span className="text-neutral-600 text-sm font-bold mr-1">
              Fallback:
            </span>
            <span className="text-sm">{formatTimezone(window.fallbackTz)}</span>
          </div>
        )}
      </div>
    ),
    usHolidays: window.usHolidays ? 'Yes' : 'No',
    actions: (
      <div
        className="flex justify-center items-center"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <SideDropActions
          options={[
            {
              label: 'Edit',
              Icon: Pencil,
              show: can(role, 'update', Subject.CAMPAIGNS),
              onClick: () =>
                navigate(
                  `/beta/campaigns/contact-window?id=${window.id}&returnTo=/beta/campaigns/contact-windows`,
                ),
            },
          ]}
        />
      </div>
    ),
  }));

  return (
    <>
      <Heading
        title="Contact Windows"
        subtitle="Manage contact windows for your campaigns."
      />

      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-end mb-6">
          <Can operation="create" subject={Subject.CAMPAIGNS}>
            <Button
              onClick={() =>
                navigate(
                  '/beta/campaigns/contact-window?returnTo=/beta/campaigns/contact-windows',
                )
              }
            >
              Create Contact Window
            </Button>
          </Can>
        </div>

        <Can operation="read" subject={Subject.CAMPAIGNS}>
          <Table
            headers={headers}
            rows={rows}
            totalItems={contactWindows.length}
            currentPage={1}
            onPageChange={() => {}}
            onSort={() => {}}
            loading={loading}
            defaultSize={pageSize}
            setSize={setPageSize}
            sortConfig={{ key: null, direction: null }}
          />
        </Can>
      </ContentWrapper>
    </>
  );
};
